import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";

import { WorkspaceMenu } from "./WorkspaceMenu";
import type { ActiveCloudSession, WorkspaceMenuItem } from "./WorkspaceMenu";

interface WorkspaceMenuTriggerProps {
  readonly items: readonly WorkspaceMenuItem[];
  readonly activeCloudSession: ActiveCloudSession | null;
  readonly onRequestPrivateWorkspace: () => void;
}

function AccountWorkspaceTriggerIcon(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="9" r="2.5" />
      <path d="M7.5 16.5c1.2-1.75 2.85-2.625 4.5-2.625s3.3.875 4.5 2.625" />
    </svg>
  );
}

/**
 * Account / Workspace footer button + popover. Owns the open/close state so the
 * popover dismisses on any click outside the menu and trigger (e.g. clicking a
 * different sidebar icon), not only on a second click of the trigger itself.
 */
export function WorkspaceMenuTrigger({
  items,
  activeCloudSession,
  onRequestPrivateWorkspace,
}: WorkspaceMenuTriggerProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the popover when a mousedown lands outside the trigger + menu. The
  // container wraps both, so a click on any other module/sidebar icon (which is
  // outside the container) dismisses the menu.
  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (event: MouseEvent): void => {
      const container = containerRef.current;
      if (container && !container.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return (): void => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => { setMenuOpen((open) => !open); }}
        className="flex h-10 w-10 items-center justify-center rounded-xl border-none bg-transparent text-content-secondary transition-colors hover:bg-surface-hover/60 hover:text-content"
        title="Account / Workspace"
        aria-label="Account / Workspace"
      >
        <AccountWorkspaceTriggerIcon />
      </button>
      {menuOpen ? (
        <WorkspaceMenu
          items={items.map((item) => ({
            ...item,
            onSelect: (): void => {
              item.onSelect();
              setMenuOpen(false);
            },
          }))}
          activeCloudSession={activeCloudSession}
          onRequestPrivateWorkspace={() => {
            onRequestPrivateWorkspace();
            setMenuOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
